// src/lib/appReview.ts
import { AppReview } from "@capawesome/capacitor-app-review";
import { Capacitor } from "@capacitor/core";

type ReviewConfig = {
  iosAppId: string; // numeric Apple ID (e.g., "123456789") for iOS fallback
  minSessions: number; // don't consider prompting before N app launches
  triggerInterval: number; // prompt on every Nth "happy" event
  maxPrompts: number; // lifetime cap
  cooldownDays: number; // minimum days between prompts
};

const LS = {
  firstSeenAt: "managd.review.firstSeenAt",
  sessions: "managd.review.sessions",
  happyEvents: "managd.review.happyEvents",
  shownCount: "managd.review.shownCount",
  lastShownAt: "managd.review.lastShownAt",
  sessionGuard: "managd.review.sessionGuard",
};

const DEFAULTS: ReviewConfig = {
  iosAppId: "000000000",
  minSessions: 3,
  triggerInterval: 5,
  maxPrompts: 3,
  cooldownDays: 90,
};

let config: ReviewConfig = { ...DEFAULTS };

/** Call once on app boot (e.g., in App.tsx). */
export function initAppReview(partial?: Partial<ReviewConfig>) {
  config = { ...DEFAULTS, ...partial };
  const now = Date.now();

  if (!localStorage.getItem(LS.firstSeenAt)) {
    localStorage.setItem(LS.firstSeenAt, String(now));
  }

  // Count one "session" per fresh app boot (guard prevents re-counting)
  if (!localStorage.getItem(LS.sessionGuard)) {
    const sessions = getNum(LS.sessions) + 1;
    localStorage.setItem(LS.sessions, String(sessions));
    localStorage.setItem(LS.sessionGuard, String(now));
  }
}

/** Call this after a "happy" moment (e.g., from your push hook). */
export async function hitHappyMoment() {
  const n = getNum(LS.happyEvents) + 1;
  localStorage.setItem(LS.happyEvents, String(n));

  if (n % config.triggerInterval !== 0) return;
  if (!eligibleToPrompt()) return;

  await requestInAppReview(); // request only; do NOT auto-open the store
}

/** Manual entry (e.g., Settings → "Rate us"). */
export async function promptForReview() {
  try {
    await openStoreReviewPage(); // guaranteed path to a review page
    bumpShown();
  } catch (e) {
    console.warn("Opening store review page failed", e);
  }
}

/** Clear all app review data — call on logout to prevent data leaking to the next user. */
export function clearAppReviewData() {
  Object.values(LS).forEach((k) => localStorage.removeItem(k));
}

/** Optional helpers for testing in dev builds. */
export const appReviewDebug = {
  resetAll() {
    Object.values(LS).forEach((k) => localStorage.removeItem(k));
  },
  stats() {
    return {
      sessions: getNum(LS.sessions),
      happyEvents: getNum(LS.happyEvents),
      shownCount: getNum(LS.shownCount),
      lastShownAt: Number(localStorage.getItem(LS.lastShownAt) ?? 0),
      config,
    };
  },
};

/* ---------------- internal helpers ---------------- */

function getNum(key: string) {
  return Number(localStorage.getItem(key) ?? 0);
}

function daysSince(ts: number) {
  return (Date.now() - ts) / (1000 * 60 * 60 * 24);
}

function eligibleToPrompt(): boolean {
  const sessions = getNum(LS.sessions);
  const shownCount = getNum(LS.shownCount);
  const lastShownAt = Number(localStorage.getItem(LS.lastShownAt) ?? 0);

  if (sessions < config.minSessions) return false;
  if (shownCount >= config.maxPrompts) return false;
  if (lastShownAt && daysSince(lastShownAt) < config.cooldownDays) return false;
  return true;
}

async function requestInAppReview() {
  try {
    await AppReview.requestReview(); // OS may silently ignore due to throttling
    bumpShown(); // count as an attempt to avoid nagging, even if OS suppresses UI
  } catch (e) {
    console.warn("requestReview failed, falling back to store", e);
    await openStoreReviewPage(); // rare error case
    bumpShown();
  }
}

async function openStoreReviewPage() {
  const isIOS = Capacitor.getPlatform() === "ios";
  await AppReview.openAppStore(
    isIOS
      ? {
          appId: config.iosAppId,
        }
      : undefined
  );
}

function bumpShown() {
  localStorage.setItem(LS.shownCount, String(getNum(LS.shownCount) + 1));
  localStorage.setItem(LS.lastShownAt, String(Date.now()));
}
